import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import ReactQuill from "react-quill";
import Swal from "sweetalert2";
import DOMPurify from "dompurify";
import quotedPrintable from "quoted-printable";
import "react-quill/dist/quill.snow.css";
import EmailEditor from "./EmailEditor";

const BASE_API_URL = process.env.REACT_APP_API_URL;

const EmailContaint = () => {
    const [name, setName] = useState("");
    const [senderEmail, setSenderEmail] = useState("");
    const [emailList, setEmailList] = useState("");
    const [scheduledDate, setScheduledDate] = useState("");
    const [emailsPerHour, setEmailsPerHour] = useState(10);
    const [emailContent, setEmailContent] = useState("");
    const [emailListData, setEmailListData] = useState([]);
    const [senderEmailsData, setSenderEmailsData] = useState([]);
    const [testEmail, setTestEmail] = useState("");

    useEffect(() => {
        fetchSelectOptions();
    }, []);

    const fetchSelectOptions = async () => {
        try {
            const emailRes = await axios.get(
                `${BASE_API_URL}/getEmailList`
            );
            setEmailListData(emailRes.data || []);

            const senderRes = await axios.get(
                `${BASE_API_URL}/sender/list`
            );
            setSenderEmailsData(senderRes.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    // ================= CLEAN HTML =================

    const decodeEmailHtml = (html) => {
        try {
            let decoded = quotedPrintable.decode(html);
            decoded = decoded.replace(/=\n/g, "");
            return decoded;
        } catch {
            return html;
        }
    };

    const getCleanHtml = () => {
        let decoded = decodeEmailHtml(emailContent);

        decoded = decoded.replace(
            /<img/gi,
            `<img style="max-width:100%;height:auto;display:block;margin:10px 0;"`
        );

        return DOMPurify.sanitize(decoded, {
            ALLOWED_TAGS: [
                "p",
                "br",
                "b",
                "i",
                "u",
                "strong",
                "em",
                "ul",
                "ol",
                "li",
                "a",
                "img",
                "blockquote",
                "h1",
                "h2",
                "h3",
                "span",
                "div"
            ],
            ALLOWED_ATTR: [
                "href",
                "src",
                "alt",
                "style",
                "target",
                "rel"
            ]
        });
    };

    // ================= SAVE CAMPAIGN =================

    const handleAddCampaign = async () => {
        if (!name || !senderEmail || !emailList) {
            Swal.fire(
                "Warning",
                "Fill all required fields",
                "warning"
            );
            return;
        }

        try {
            const payload = {
                name,
                fromEmail: senderEmail,
                emailListName: emailList,
                emailContent: getCleanHtml(),
                scheduledDate,
                emailsPerHour
            };

            await axios.post(
                `${BASE_API_URL}/campaigns/add`,
                payload
            );

            Swal.fire(
                "Success",
                "Campaign Scheduled",
                "success"
            );

            setName("");
            setSenderEmail("");
            setEmailList("");
            setScheduledDate("");
            setEmailsPerHour(10);
            setEmailContent("");
        } catch (error) {
            Swal.fire(
                "Error",
                "Failed to add campaign",
                "error"
            );
        }
    };

    // ================= TEST MAIL =================

    const handleSendTestMail = async () => {
        if (!testEmail) {
            Swal.fire(
                "Warning",
                "Enter test email",
                "warning"
            );
            return;
        }

        try {
            await axios.post(
                `${BASE_API_URL}/campaigns/send-test`,
                {
                    toEmail: testEmail,
                    fromEmail: senderEmail,
                    emailContent: getCleanHtml(),
                    name
                }
            );

            Swal.fire(
                "Success",
                "Test email sent",
                "success"
            );

            setTestEmail("");
        } catch (error) {
            Swal.fire(
                "Error",
                "Failed to send test mail",
                "error"
            );
        }
    };

    // ================= QUILL =================

    const modules = {
        toolbar: [
            [{ header: [1, 2, 3, false] }],
            ["bold", "italic", "underline", "strike"],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            ["blockquote"],
            ["link", "image"],
            ["clean"]
        ],
        clipboard: {
            matchVisual: false
        }
    };

    const formats = [
        "header",
        "bold",
        "italic",
        "underline",
        "strike",
        "color",
        "background",
        "align",
        "list",
        "bullet",
        "blockquote",
        "link",
        "image"
    ];

    return (
        <div className="p-3 border rounded bg-light">
            <Form>
                {/* Row 1 */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>
                                Campaign Name
                            </Form.Label>
                            <Form.Control
                                value={name}
                                onChange={(e) =>
                                    setName(
                                        e.target.value
                                    )
                                }
                                placeholder="Enter campaign name"
                            />
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>
                                Sender Email
                            </Form.Label>
                            <Form.Select
                                value={senderEmail}
                                onChange={(e) =>
                                    setSenderEmail(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    Select sender email
                                </option>

                                {senderEmailsData.map(
                                    (item) => (
                                        <option
                                            key={
                                                item.id
                                            }
                                            value={
                                                item.email
                                            }
                                        >
                                            {
                                                item.email
                                            }
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>

                {/* Row 2 */}
                <Row className="mb-3">
                    <Col md={6}>
                        <Form.Group>
                            <Form.Label>
                                Email List
                            </Form.Label>

                            <Form.Select
                                value={emailList}
                                onChange={(e) =>
                                    setEmailList(
                                        e.target.value
                                    )
                                }
                            >
                                <option value="">
                                    Select email list
                                </option>

                                {emailListData.map(
                                    (item) => (
                                        <option
                                            key={
                                                item.emailListId
                                            }
                                            value={
                                                item.emailListName
                                            }
                                        >
                                            {
                                                item.emailListName
                                            }
                                        </option>
                                    )
                                )}
                            </Form.Select>
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>
                                Schedule Date
                            </Form.Label>

                            <Form.Control
                                type="date"
                                value={
                                    scheduledDate
                                }
                                onChange={(e) =>
                                    setScheduledDate(
                                        e.target.value
                                    )
                                }
                            />
                        </Form.Group>
                    </Col>

                    <Col md={3}>
                        <Form.Group>
                            <Form.Label>
                                Emails / Hour
                            </Form.Label>

                            <Form.Control
                                type="number"
                                min="1"
                                value={
                                    emailsPerHour
                                }
                                onChange={(e) =>
                                    setEmailsPerHour(
                                        e.target.value
                                    )
                                }
                            />

                            <small className="text-muted">
                                Interval:{" "}
                                {emailsPerHour >
                                    0
                                    ? Math.floor(
                                        60 /
                                        emailsPerHour
                                    )
                                    : 0}{" "}
                                min/email
                            </small>
                        </Form.Group>
                    </Col>
                </Row>

                {/* Editor */}
                {/* <Form.Group className="mb-3">
                    <Form.Label>
                        Email Content
                    </Form.Label>

                    <ReactQuill
                        theme="snow"
                        value={emailContent}
                        onChange={
                            setEmailContent
                        }
                        modules={modules}
                        formats={formats}
                        placeholder="Write or paste your email content here..."
                         style={{
                            background: "white",
                            minHeight: "320px"
                        }}
                    />
                </Form.Group> */}
                <Form.Group className="mb-3">
                    <Form.Label>Email Content</Form.Label>

                    <EmailEditor
                        emailContent={emailContent}
                        setEmailContent={setEmailContent}
                    />
                </Form.Group>

                {/* Bottom */}
                <Row className="mt-4 align-items-end">
                    <Col md={8}>
                        <Form.Group>
                            <Form.Label>
                                Send Test Email
                            </Form.Label>

                            <Row>
                                <Col md={8}>
                                    <Form.Control
                                        type="email"
                                        placeholder="Enter test email"
                                        value={
                                            testEmail
                                        }
                                        onChange={(
                                            e
                                        ) =>
                                            setTestEmail(
                                                e
                                                    .target
                                                    .value
                                            )
                                        }
                                    />
                                </Col>

                                <Col md={4}>
                                    <Button
                                        className="w-100"
                                        variant="primary"
                                        onClick={
                                            handleSendTestMail
                                        }
                                    >
                                        Send Test Mail
                                    </Button>
                                </Col>
                            </Row>
                        </Form.Group>
                    </Col>

                    <Col md={4} className="text-end">
                        <Button
                            variant="success"
                            onClick={
                                handleAddCampaign
                            }
                        >
                            <FontAwesomeIcon
                                icon={
                                    faPaperPlane
                                }
                                className="me-2"
                            />
                            Send Campaign
                        </Button>
                    </Col>
                </Row>
            </Form>

            <style>{`
                .ql-editor{
                    min-height:320px;
                    word-break:break-word;
                }

                .ql-editor img{
                    max-width:100%;
                    height:auto;
                }
            `}</style>
        </div>
    );
};

export default EmailContaint;